import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AppButton,
  AppCheckbox,
  AppModal,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppSpin,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import {
  useConsentDocumentPrintInit,
  useConsentDocumentPrintOutput,
} from '@/hooks/main/useConsentDocumentPrint';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import type { ConsentDocumentPrintPayload } from '@/pages/main/consent/services/print';

type PrintDocumentType = 1 | 2;

const MASSAGER_LOCATION_REQUIRED_MESSAGE = '施術師及び拠点住所を選択してください。';

interface ConsentDocumentPrintModalProps {
  consentId: number;
  documentTypeId: PrintDocumentType;
  isOpen: boolean;
  onClose: () => void;
}

const ConsentDocumentPrintModal: React.FC<ConsentDocumentPrintModalProps> = ({
  consentId,
  documentTypeId,
  isOpen,
  onClose,
}) => {
  const appModal = useAppModal();
  const toast = useAppToast();

  const initQuery = useConsentDocumentPrintInit(consentId, documentTypeId, isOpen);
  const outputMutation = useConsentDocumentPrintOutput(consentId);
  const initData = initQuery.data;

  const [form, setForm] = useState<ConsentDocumentPrintPayload | null>(null);
  const [isRepresentativeNameEdited, setIsRepresentativeNameEdited] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const registeredRepresentativeName = useMemo(
    () =>
      initData?.document_templates?.find(t => !!t.representative_name?.trim())
        ?.representative_name ?? '',
    [initData?.document_templates]
  );

  const getTemplateRepresentativeName = useCallback(
    (representativeName?: string | null) =>
      representativeName?.trim() ? representativeName : registeredRepresentativeName,
    [registeredRepresentativeName]
  );

  useEffect(() => {
    if (!isOpen || !initData) {
      return;
    }

    const defaultTemplate = (initData.document_templates ?? []).find(t => t.is_default);
    const hasDefaultTemplate = !!defaultTemplate;

    setForm({
      document_type_id: initData.document_type_id,
      document_template_id: hasDefaultTemplate ? defaultTemplate!.id : 0,
      hospital_doctor_name: (
        initData.hospital_doctor_name?.trim() ||
        `${initData.hospital_name ?? ''} ${initData.doctor_name ?? ''}`.trim()
      ).slice(0, 255),
      hospital_name: initData.hospital_name ?? '',
      doctor_name: initData.doctor_name ?? '',
      suffix_type_id: hasDefaultTemplate ? (defaultTemplate!.suffix_type_id ?? 0) : 0,
      representative_name: getTemplateRepresentativeName(defaultTemplate?.representative_name),
      massager_id: 0,
      massager_location_id: 0,
      is_print: initData.is_print ?? true,
      is_representative_name: initData.is_representative_name ?? false,
      is_massager: initData.is_massager ?? false,
      is_practitioner_name: initData.is_practitioner_name ?? false,
      is_massager_location: initData.is_massager_location ?? false,
      is_practitioner_address: initData.is_practitioner_address ?? false,
      is_massager_location_address: initData.is_massager_location_address ?? false,
    });
    setIsRepresentativeNameEdited(false);
    setLocationError(null);
  }, [isOpen, initData, getTemplateRepresentativeName]);

  const templateOptions = useMemo(
    () =>
      (initData?.document_templates ?? []).map(t => ({
        value: t.id,
        label: t.title,
      })),
    [initData?.document_templates]
  );

  const suffixOptions = useMemo(
    () =>
      (initData?.suffix_types ?? []).map(s => ({
        value: s.id,
        label: s.name,
      })),
    [initData?.suffix_types]
  );

  const massagerOptions = useMemo(
    () =>
      (initData?.massagers ?? []).map(m => {
        const mWithAddress = m as typeof m & {
          address1?: string | null;
          address2?: string | null;
          address3?: string | null;
        };

        return {
          value: m.id,
          label:
            [mWithAddress.address1, mWithAddress.address2, mWithAddress.address3]
              .filter(part => !!part && `${part}`.trim() !== '')
              .join(' ') || `${m.serial_number ?? ''}:${m.name}`,
        };
      }),
    [initData?.massagers]
  );

  const massagerAddressOptions = useMemo(() => {
    if (!form?.massager_id) return [];

    return (initData?.massager_location_options ?? [])
      .filter(option => option.massager_id === form.massager_id)
      .map(option => ({
        value: option.id,
        label: option.label,
      }));
  }, [initData?.massager_location_options, form?.massager_id]);

  // Enable 拠点住所 select only when the chosen 施術師 has 2+ 拠点 (manual pick).
  // With 1 拠点 it stays disabled and shows the auto-selected value.
  const isMassagerAddressEnabled = !!form?.massager_id && massagerAddressOptions.length > 1;

  const updateTemplateDependents = (templateId: number) => {
    if (!form) return;
    if (!templateId) {
      setForm({
        ...form,
        document_template_id: 0,
        suffix_type_id: 0,
        representative_name: registeredRepresentativeName,
      });
      return;
    }

    const selected = initData?.document_templates?.find(t => t.id === templateId);
    if (!selected) return;

    setForm({
      ...form,
      document_template_id: templateId,
      suffix_type_id: selected.suffix_type_id ?? form.suffix_type_id,
      representative_name: isRepresentativeNameEdited
        ? (form.representative_name ?? '')
        : getTemplateRepresentativeName(selected.representative_name),
    });
  };

  const onSubmit = async () => {
    if (!form) return;
    if (!form.document_template_id) {
      toast.error('テンプレートを選択してください。');
      return;
    }
    if (!form.hospital_doctor_name) {
      toast.error('宛名を入力してください。');
      return;
    }
    if (!form.suffix_type_id) {
      toast.error('敬称を選択してください。');
      return;
    }
    const requiresMassagerLocation =
      !!form.is_massager_location || !!form.is_massager_location_address;
    if (requiresMassagerLocation && (!form.massager_id || !form.massager_location_id)) {
      appModal.error({ title: MASSAGER_LOCATION_REQUIRED_MESSAGE, okText: 'OK' });
      if (form.massager_id && !form.massager_location_id) {
        setLocationError(MASSAGER_LOCATION_REQUIRED_MESSAGE);
      }
      return;
    }
    if (form.is_massager && !form.massager_id) {
      appModal.error({ title: '施術師を選択してください。', okText: 'OK' });
      return;
    }
    if (massagerAddressOptions.length > 1 && !form.massager_location_id) {
      setLocationError('拠点は必須です。');
      return;
    }
    setLocationError(null);

    try {
      const blob = await outputMutation.mutateAsync(form);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'PDF出力に失敗しました。'));
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={documentTypeId === 1 ? '御礼状出力' : '発行依頼書出力'}
      maxWidth={920}
      footer={[
        <AppButton
          key="output"
          type="primary"
          onClick={onSubmit}
          loading={outputMutation.isPending}
          disabled={initQuery.isLoading || !form}
        >
          出力
        </AppButton>,
      ]}
    >
      <div className="relative min-h-[200px]">
        {initQuery.isLoading || !form ? (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70 backdrop-blur-sm"
            aria-busy="true"
            aria-label="読み込み中"
          >
            <AppSpin size="large" />
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-3 p-2">
            <div className="col-span-12">
              <AppSelect
                value={form.document_template_id || undefined}
                options={templateOptions}
                onChange={v => updateTemplateDependents(v ? Number(v) : 0)}
                className="w-full"
                placeholder="テンプレートを選択してください"
                notFoundContent="データがありません"
                allowClear={false}
              />
            </div>

            <div className="col-span-8">
              <AppTextArea
                value={form.hospital_doctor_name}
                onChange={e => setForm({ ...form, hospital_doctor_name: e.target.value })}
                placeholder="宛名"
                maxLength={255}
                autoSize={{ minRows: 1, maxRows: 3 }}
              />
            </div>

            <div className="col-span-4 flex items-center">
              <AppRadioGroup
                value={form.suffix_type_id || undefined}
                onChange={e => setForm({ ...form, suffix_type_id: Number(e.target.value) })}
              >
                {suffixOptions.map(option => (
                  <AppRadio key={option.value} value={option.value}>
                    {option.label}
                  </AppRadio>
                ))}
              </AppRadioGroup>
            </div>
            {documentTypeId !== 1 && (
              <div className="col-span-12">
                <AppCheckbox
                  checked={!!form.is_print}
                  onChange={e => setForm({ ...form, is_print: e.target.checked })}
                >
                  患者住所・電話番号を印字する
                </AppCheckbox>
              </div>
            )}
            <div className="col-span-6">
              <AppTextField
                value={form.representative_name ?? ''}
                onChange={e => {
                  setIsRepresentativeNameEdited(true);
                  setForm({ ...form, representative_name: e.target.value });
                }}
                placeholder="代表者氏名"
                maxLength={255}
              />
            </div>

            <div className="col-span-6">
              <div className="flex flex-col gap-2">
                <AppSelect
                  value={form.massager_id || undefined}
                  options={massagerOptions}
                  onChange={v => {
                    const nextMassagerId = Number(v);
                    // Auto-select 拠点 only when the chosen 施術師 has exactly one location.
                    // With 2+ locations, leave empty so the user picks manually.
                    const addressOptionsForMassager = (
                      initData?.massager_location_options ?? []
                    ).filter(option => option.massager_id === nextMassagerId);

                    setForm({
                      ...form,
                      massager_id: nextMassagerId,
                      massager_location_id:
                        addressOptionsForMassager.length === 1
                          ? addressOptionsForMassager[0].id
                          : 0,
                    });
                    setLocationError(null);
                  }}
                  className="w-full"
                  placeholder="施術師を選択してください。"
                  notFoundContent="データがありません"
                  allowClear={false}
                />
                <div>
                  <AppSelect
                    value={form.massager_location_id || undefined}
                    options={massagerAddressOptions}
                    onChange={v => {
                      setForm({
                        ...form,
                        massager_location_id: Number(v),
                      });
                      setLocationError(null);
                    }}
                    className="w-full"
                    placeholder="施術師の拠点住所を選択してください。"
                    notFoundContent="データがありません"
                    disabled={!isMassagerAddressEnabled}
                    allowClear={false}
                    status={locationError ? 'error' : undefined}
                  />
                  {locationError && (
                    <p className="mt-1 text-sm text-red-500">{locationError}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_representative_name}
                onChange={e =>
                  setForm({
                    ...form,
                    is_representative_name: e.target.checked,
                    is_massager: e.target.checked ? false : form.is_massager,
                  })
                }
              >
                代表者氏名を印字する
              </AppCheckbox>
            </div>
            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_massager}
                onChange={e =>
                  setForm({
                    ...form,
                    is_massager: e.target.checked,
                    is_representative_name: e.target.checked ? false : form.is_representative_name,
                  })
                }
              >
                施術師名を印字する
              </AppCheckbox>
            </div>

            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_practitioner_name}
                onChange={e =>
                  setForm({
                    ...form,
                    is_practitioner_name: e.target.checked,
                    is_massager_location: e.target.checked ? false : form.is_massager_location,
                  })
                }
              >
                施術所名を印字する
              </AppCheckbox>
            </div>
            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_massager_location}
                onChange={e =>
                  setForm({
                    ...form,
                    is_massager_location: e.target.checked,
                    is_practitioner_name: e.target.checked ? false : form.is_practitioner_name,
                  })
                }
              >
                施術師拠点名を印字する
              </AppCheckbox>
            </div>

            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_practitioner_address}
                onChange={e =>
                  setForm({
                    ...form,
                    is_practitioner_address: e.target.checked,
                    is_massager_location_address: e.target.checked
                      ? false
                      : form.is_massager_location_address,
                  })
                }
              >
                施術所住所・電話番号を印字する
              </AppCheckbox>
            </div>
            <div className="col-span-6">
              <AppCheckbox
                checked={!!form.is_massager_location_address}
                onChange={e =>
                  setForm({
                    ...form,
                    is_massager_location_address: e.target.checked,
                    is_practitioner_address: e.target.checked
                      ? false
                      : form.is_practitioner_address,
                  })
                }
              >
                施術師拠点の住所・電話番号を印字する
              </AppCheckbox>
            </div>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default ConsentDocumentPrintModal;
